import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { InvitePreviewBody } from './MedicalStaffInvitePreviewModal';

// Pre-send confirmation modal for the resend-invitation flow. Separate component
// from the invite modal (per product decision) but shares the same email card —
// here in its `resend` variant (재발송 badge + "previous link invalidated" notice).
// Populated with the target staff's stored values from the edit panel.

const I18N = 'medicalStaffManagement.invitePreviewModal';

interface MedicalStaffResendPreviewModalProps {
  open: boolean;
  name: string;
  email: string;
  departmentLabel: string;
  jobRoleLabel: string;
  sentAt: string;
  submitting: boolean;
  // [← 수정] — close the preview without resending.
  onEdit: () => void;
  // [✉ 재발송] — run the actual resend.
  onConfirm: () => void;
}

export function MedicalStaffResendPreviewModal({
  open,
  name,
  email,
  departmentLabel,
  jobRoleLabel,
  sentAt,
  submitting,
  onEdit,
  onConfirm,
}: MedicalStaffResendPreviewModalProps) {
  const { t } = useTranslation('admin');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onEdit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onEdit]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-overlay modal-overlay--visible"
      onClick={onEdit}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="modal"
        style={{
          maxWidth: 640,
          width: '92vw',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '88vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 className="modal__title">{t(`${I18N}.resendTitle`)}</h2>
        </div>
        <div className="modal__body" style={{ overflowY: 'auto' }}>
          <InvitePreviewBody
            name={name}
            email={email}
            departmentLabel={departmentLabel}
            jobRoleLabel={jobRoleLabel}
            sentAt={sentAt}
            resend
          />
        </div>
        <div className="modal__footer">
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={onEdit}
            disabled={submitting}
          >
            {t(`${I18N}.editButton`)}
          </button>
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={onConfirm}
            disabled={submitting}
          >
            {t(`${I18N}.resendButton`)}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
